import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Bell, Check, ChevronDown, Clock, Download, MessageSquare, Search, ShieldAlert, X, AlertTriangle, AlertCircle, Info, Filter } from "lucide-react";
import { useAdapter, useSiteStore, useTopology } from "@/store/site";
import { PageShell } from "./PageShell";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { KpiStrip } from "@/components/ui/KpiStrip";
import { HoldToConfirm } from "@/components/ui/HoldToConfirm";
import { cn } from "@/lib/cn";
import { carryTo } from "@/lib/carryParams";
import { tr, useT } from "@/lib/i18n";
import { formatRelativeAge, formatDateTime, formatRelativeFuture } from "@/lib/format";
import { exportAlarmsCsv } from "@/lib/exportCsv";
import { SEV_ORDER, severityBadgeClass } from "@/lib/severity";
import { pushToast } from "@/hooks/useToasts";
import { useNow } from "@/hooks/useNow";
import { useUrlParamState } from "@/hooks/useUrlParamState";
import { useWorstAlarm } from "@/hooks/useWorstAlarm";
import type { EmsAdapter } from "@/data/adapter";
import type { Alarm, AlarmComment, AlarmStatus, ControllerMode } from "@/data/types";

type Tab = "active" | "snoozed" | "acknowledged" | "cleared" | "all";
const TABS: Tab[] = ["active", "snoozed", "acknowledged", "cleared", "all"];
const SORT_MODES = ["severity", "newest", "oldest"] as const;
type SortMode = (typeof SORT_MODES)[number];
const SEVERITIES = ["critical", "major", "minor", "warning"] as const;

const SEV_ICON = { critical: ShieldAlert, major: AlertTriangle, minor: AlertCircle, warning: Info } as const;

// Tab labels are translated at render; the Tab VALUES stay English (they
// are the ?tab= URL params). Same for severities — manage.sev.* is chrome
// only, ?severity= keeps the wire value.
const TAB_LABEL_KEYS: Record<Tab, string> = {
  active: "alarms.active",
  snoozed: "manage.alarms.kpi.snoozed",
  acknowledged: "alarms.acknowledged",
  cleared: "alarms.cleared",
  all: "manage.alarms.tab.all",
};

// Quick-note template DICT KEYS — translated at render time (chip shows
// the part before " — "; the full translated text is what gets posted).
const NOTE_TEMPLATE_KEYS = [
  "manage.alarms.note.onsite",
  "manage.alarms.note.remote",
  "manage.alarms.note.oem",
  "manage.alarms.note.selfResolve",
  "manage.alarms.note.runbook",
];

const SNOOZE_OPTIONS: { label: string; ms: number }[] = [
  { label: "1 h", ms: 60 * 60_000 },
  { label: "4 h", ms: 4 * 60 * 60_000 },
  { label: "12 h", ms: 12 * 60 * 60_000 },
  { label: "24 h", ms: 24 * 60 * 60_000 },
];

/* ------------------------------------------------------------------------ */
/* Adapter capabilities (ALM-03)                                             */
/* ------------------------------------------------------------------------ */

/** What the current backend can ACTUALLY do — drives hiding placebo UI. */
type AlarmCapabilities = { snoozeAlarms: boolean; alarmComments: boolean };

/**
 * The capability flags + optional comment read-back are being added to the
 * adapter contract (adapter.ts + both adapters) in a parallel change; this
 * tolerant view compiles before and after that lands. Until the flags
 * exist we assume FULL capability so behaviour is unchanged (mock parity).
 */
type AdapterWithCaps = EmsAdapter & {
  capabilities?: AlarmCapabilities;
  /** Lazy comment read-back for live alarms (TB GET /api/alarm/{id}/comment). */
  alarmComments?: (id: string) => Promise<AlarmComment[]>;
};

function alarmCaps(adapter: EmsAdapter): AlarmCapabilities {
  return (adapter as AdapterWithCaps).capabilities ?? { snoozeAlarms: true, alarmComments: true };
}

/**
 * Live alarms whose rule type TB doesn't expose arrive with an empty or
 * "unknown" rule_type (the adapter no longer asserts "threshold" — ALM-04).
 * A wrong label is worse than no label: render NO chip in that case.
 */
function knownRuleType(rt: string | undefined | null): boolean {
  return !!rt && rt !== "unknown";
}

/**
 * Per-rule-type "Suggested next step" DICT KEYS — translated at render time
 * (F09). Keyed on Alarm["rule_type"] (NOT AlarmRule["type"]) so the compiler
 * enforces exhaustiveness against the actual alarm union the adapters emit;
 * the two vocabularies diverge (alarm "rate" vs rule "rate_of_change", and
 * the alarm union has no composite/comm_degradation). Copy is generic + safe
 * operator triage guidance only — never site-specific fabrication.
 */
const RULE_TYPE_NEXTSTEP_KEYS: Record<Alarm["rule_type"], string> = {
  threshold: "manage.alarms.nextStep.threshold",
  range: "manage.alarms.nextStep.range",
  boolean: "manage.alarms.nextStep.boolean",
  inactivity: "manage.alarms.nextStep.inactivity",
  stuck: "manage.alarms.nextStep.stuck",
  rate: "manage.alarms.nextStep.rate",
  delta: "manage.alarms.nextStep.delta",
  daily_energy: "manage.alarms.nextStep.daily_energy",
  excessive_cycling: "manage.alarms.nextStep.excessive_cycling",
  peak_demand: "manage.alarms.nextStep.peak_demand",
  idle_consumption: "manage.alarms.nextStep.idle_consumption",
  expression: "manage.alarms.nextStep.expression",
};

/**
 * Resolve the next-step key for a rule type; an unknown/unexpected type
 * (TB can emit an empty/novel rule_type) falls back to the generic line
 * rather than rendering a blank or throwing.
 */
function nextStepKey(rt: Alarm["rule_type"]): string {
  return knownRuleType(rt) && rt in RULE_TYPE_NEXTSTEP_KEYS
    ? RULE_TYPE_NEXTSTEP_KEYS[rt]
    : "manage.alarms.nextStep.generic";
}

/**
 * "Adjust dispatch →" lever (N08 + N25). Only ACTION-SHAPED rule types — the
 * ones where re-dispatching the plant is the real remedy — get the lever; a
 * threshold/comm/stuck sensor fault never invites a needless manual override
 * (N08 risk note). The Partial map is deliberate: absent keys render no lever.
 *
 *   - peak_demand: the named lever (N25). The link pre-picks the PeakShaving
 *     mode via SchedulePage's `?suggest=` contract (mints a draft entry +
 *     opens the EntryEditor), so the FM lands on the exact remedy.
 *   - excessive_cycling / idle_consumption: dispatch IS the remedy but the
 *     specific mode is a weaker fit (N25), so they open the OverridePanel via
 *     `?override=1` (N08) without assuming a mode.
 *
 * Both variants carry ONLY the dispatch intent — never the alarm's forensic
 * time window (schedule ignores RANGE_KEYS, and `{ override }`/`{ suggest }`
 * keeps the intent clean — N08 risk note).
 */
type DispatchLever =
  | { suggestMode: ControllerMode }   // N25 — pre-pick a schedule-draft mode
  | { override: true };               // N08 — open the OverridePanel, no mode

const ACTION_LEVER: Partial<Record<Alarm["rule_type"], DispatchLever>> = {
  peak_demand: { suggestMode: "PeakShaving" },
  excessive_cycling: { override: true },
  idle_consumption: { override: true },
};

/** The dispatch lever for a non-cleared, action-shaped alarm — else null. */
function dispatchLeverFor(alarm: Alarm): DispatchLever | null {
  if (alarm.status === "cleared") return null;
  return ACTION_LEVER[alarm.rule_type] ?? null;
}

/** Stable fallback so an unresolved query doesn't mint a new [] per render. */
const NO_ALARMS: Alarm[] = [];

export default function AlarmsPage() {
  const t = useT();
  const adapter = useAdapter();
  const topology = useTopology();
  const operator = useSiteStore((s) => s.operator);
  const caps = alarmCaps(adapter);
  const qc = useQueryClient();
  const now = useNow(5_000);
  // SHARED alarms cache (CONS-05 / ALM-10) — same key as useAlarms(), so a
  // cache warmed by Overview/HealthBanner renders instantly on mount (no
  // false "No alarms" flash) and every mutation invalidation below updates
  // those consumers immediately. This page-level observer just polls
  // faster (4 s incident cadence vs the app-wide 15 s); TanStack v5 runs
  // each observer's interval against the one shared cache entry.
  const { data: alarmsData } = useQuery<Alarm[]>({
    queryKey: ["alarms", topology.id],
    queryFn: () => adapter.alarms(),
    refetchInterval: 4_000,
    staleTime: 2_000,
  });
  // `undefined` = first fetch still in flight → loading UI, never a false
  // all-clear (CONS-05).
  const loaded = alarmsData !== undefined;
  const alarms = alarmsData ?? NO_ALARMS;
  // Filters + expanded row live in the URL (replace:true) so triage context
  // is deep-linkable and survives the alarm → BESS → back round trip
  // (ALM-02, LINK-09, PERS-01). Defaults encode as null to keep URLs clean.
  const [tab, setTab] = useUrlParamState<Tab>(
    "tab",
    (raw) => {
      const t = TABS.includes(raw as Tab) ? (raw as Tab) : "active";
      return t === "snoozed" && !caps.snoozeAlarms ? "active" : t;
    },
    (v) => (v === "active" ? null : v),
  );
  const [filter, setFilter] = useUrlParamState<string>(
    "q",
    (raw) => raw ?? "",
    (v) => (v ? v : null),
  );
  // Device ids are dynamic (discovered from the alarm list), so the value is
  // taken as-is; an unknown id just yields zero rows + the "clear" affordance.
  const [device, setDevice] = useUrlParamState<string | null>(
    "device",
    (raw) => raw,
    (v) => v,
  );
  const [severity, setSeverity] = useUrlParamState<string | null>(
    "severity",
    (raw) => (raw && SEVERITIES.includes(raw as (typeof SEVERITIES)[number]) ? raw : null),
    (v) => v,
  );
  const [sortMode, setSortMode] = useUrlParamState<SortMode>(
    "sort",
    (raw) => (SORT_MODES.includes(raw as SortMode) ? (raw as SortMode) : "severity"),
    (v) => (v === "severity" ? null : v),
  );
  // ?id=<alarmId> — the expanded row, mirrored to the URL so toasts,
  // AlarmsCompact rows and the palette can deep-link straight to one alarm.
  const [openId, setOpenId] = useUrlParamState<string | null>(
    "id",
    (raw) => raw,
    (v) => v,
  );
  const [comment, setComment] = useState("");
  // The draft is shared state but scoped to ONE row: clear it whenever the
  // expanded alarm changes so alarm A's note can't pre-fill alarm B's composer.
  useEffect(() => setComment(""), [openId]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [highlightId, setHighlightId] = useState<string | null>(null);
  // ?id= present at mount (or arriving later) → scroll + highlight once.
  const pendingScrollId = useRef<string | null>(openId);
  // N24a — the search + device + severity + sort controls now live behind a
  // "Filters" disclosure popover (the HelpHint idiom: a button + fixed-inset
  // backdrop that works in the sandboxed TB iframe). The tab strip stays the
  // primary first-look control; Export stays pinned in the actions tray.
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Count of non-default filters set, for the button's badge.
  const activeFilterCount =
    (filter ? 1 : 0) + (device ? 1 : 0) + (severity ? 1 : 0) + (sortMode !== "severity" ? 1 : 0);
  // N24b — answer-first banner: the worst ACTIVE alarm (any severity, since the
  // FM triages majors/minors too), reusing useWorstAlarm so it can never cite a
  // different incident than HealthBanner / IncidentStrip. Mirror AlarmsCompact's
  // snooze filter (don't nag for deliberately-snoozed alarms). Guarded to the
  // triage tabs below — no live "what do I do" applies on cleared/all.
  const { top: worstActive } = useWorstAlarm();
  const topActive =
    worstActive && !(worstActive.snoozed_until && worstActive.snoozed_until > now)
      ? worstActive
      : undefined;
  const showAdvisor = topActive !== undefined && tab !== "cleared" && tab !== "all";

  const stats = useMemo(() => {
    return {
      active: alarms.filter((a) => a.status === "active" && (!a.snoozed_until || a.snoozed_until <= now)).length,
      snoozed: alarms.filter((a) => a.status === "active" && a.snoozed_until && a.snoozed_until > now).length,
      acknowledged: alarms.filter((a) => a.status === "acknowledged").length,
      cleared: alarms.filter((a) => a.status === "cleared").length,
      critical: alarms.filter((a) => a.severity === "critical" && a.status === "active").length,
    };
  }, [alarms, now]);

  // Unique (device_id, device_name) pairs (CONS-13): the filter <select>
  // and the "filtered" chip show the same friendly name as the alarm rows;
  // the raw id stays the option VALUE so ?device= deep links are unchanged.
  const devices = useMemo(() => {
    const byId = new Map<string, string>();
    for (const a of alarms) if (!byId.has(a.device_id)) byId.set(a.device_id, a.device_name);
    return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [alarms]);
  // Echo the friendly name for the active filter; an unknown ?device= id
  // (stale deep link) falls back to the raw id rather than a blank chip.
  const deviceName = device ? devices.find((d) => d.id === device)?.name ?? device : null;

  const visible = alarms
    .filter((a) => {
      if (tab === "all") return true;
      if (tab === "snoozed") return a.status === "active" && a.snoozed_until && a.snoozed_until > now;
      if (tab === "active") return a.status === "active" && (!a.snoozed_until || a.snoozed_until <= now);
      return a.status === tab;
    })
    .filter((a) => !device || a.device_id === device)
    .filter((a) => !severity || a.severity === severity)
    .filter((a) => !filter || (a.message + a.detail + a.device_name + a.source_topic).toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => {
      if (sortMode === "severity") return (SEV_ORDER[b.severity] - SEV_ORDER[a.severity]) || (b.raised_at - a.raised_at);
      if (sortMode === "newest") return b.raised_at - a.raised_at;
      return a.raised_at - b.raised_at;
    });

  // Late-arriving ?id= (toast clicked while already on /alarms, palette):
  // adopt it into the expanded-row state and re-arm the scroll/highlight.
  const [params] = useSearchParams();
  const urlId = params.get("id");
  useEffect(() => {
    if (urlId && urlId !== openId) {
      setOpenId(urlId);
      pendingScrollId.current = urlId;
    }
    // openId deliberately excluded — this effect only adopts URL → state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlId]);

  // Consume a pending ?id= once the alarm list contains it: make sure the
  // row is visible, scroll it to center and ring-highlight it for 2 s.
  useEffect(() => {
    const target = pendingScrollId.current;
    if (!target || alarms.length === 0) return;
    pendingScrollId.current = null;
    if (!alarms.some((a) => a.id === target)) return;
    if (!visible.some((a) => a.id === target)) {
      // The target exists but a filter hides it — clear EVERY filter that
      // can hide a row (tab, severity, device, text), not just the tab,
      // or the deep link silently does nothing.
      setTab("all");
      setSeverity(null);
      setDevice(null);
      setFilter("");
    }
    setHighlightId(target);
    window.setTimeout(() => setHighlightId(null), 2000);
    // Next tick so a tab switch above has rendered the row first.
    window.setTimeout(() => {
      document.getElementById(`alarm-row-${target}`)?.scrollIntoView({ block: "center" });
    }, 60);
    // visible/setTab change identity every render; the pending ref gates re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarms, urlId]);

  function toggleSelect(id: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function selectAllVisible() {
    if (selected.size === visible.length) setSelected(new Set());
    else setSelected(new Set(visible.map((a) => a.id)));
  }
  // N24b — "Review" from the answer-first banner: expand the cited row and
  // re-arm the existing ?id= scroll/highlight path (which also clears any
  // filter hiding the row). No URL semantics change until the operator acts.
  function reviewAlarm(id: string) {
    setOpenId(id);
    pendingScrollId.current = id;
    setHighlightId(id);
    window.setTimeout(() => setHighlightId(null), 2000);
    window.setTimeout(() => {
      document.getElementById(`alarm-row-${id}`)?.scrollIntoView({ block: "center" });
    }, 60);
  }

  // Scoped, immediate cache refresh (ALM-10): every mutation invalidates
  // the shared ["alarms", <site>] query so this page, HealthBanner, the
  // Overview compact list and the toast watcher all converge at once —
  // without the unscoped invalidateQueries() refetch storm bulkAck used
  // to trigger across history/schedule/rules queries.
  function refreshAlarms() {
    return qc.invalidateQueries({ queryKey: ["alarms", topology.id] });
  }

  // Every write below follows the OverridePanel/SchedulePage house pattern:
  // try/catch with a critical toast naming the failed action — a rejected
  // adapter call must never look like success (the success toast is only
  // pushed after the await resolves). Bulk handlers keep the selection on
  // failure so the operator can retry, and still refresh so any partial
  // successes show truthfully.
  const errMsg = (err: unknown) => (err instanceof Error ? err.message : String(err));

  async function ack(a: Alarm) {
    try {
      await adapter.ackAlarm(a.id, operator);
    } catch (err) {
      pushToast({ level: "critical", title: tr("manage.alarms.ackFailed"), detail: errMsg(err) });
      return;
    }
    await refreshAlarms();
  }
  async function clear(a: Alarm) {
    try {
      await adapter.clearAlarm(a.id, operator);
    } catch (err) {
      pushToast({ level: "critical", title: tr("manage.alarms.clearFailed"), detail: errMsg(err) });
      return;
    }
    await refreshAlarms();
  }
  async function snooze(a: Alarm, ms: number) {
    try {
      await adapter.snoozeAlarm(a.id, operator, Date.now() + ms);
    } catch (err) {
      pushToast({ level: "critical", title: tr("manage.alarms.snoozeFailed"), detail: errMsg(err) });
      return;
    }
    pushToast({
      level: "info",
      title: tr("manage.alarms.snoozedToast", { device: a.device_name }),
      detail: tr("manage.alarms.snoozedDetail", {
        message: a.message,
        duration: SNOOZE_OPTIONS.find((s) => s.ms === ms)?.label ?? tr("manage.alarms.aWhile"),
      }),
    });
    await refreshAlarms();
  }
  async function bulkAck() {
    const targets = visible.filter((a) => selected.has(a.id) && a.status === "active");
    try {
      await Promise.all(targets.map((a) => adapter.ackAlarm(a.id, operator)));
      pushToast({
        level: "ok",
        title: targets.length === 1 ? tr("manage.alarms.ackOk.one") : tr("manage.alarms.ackOk.many", { count: targets.length }),
        detail: tr("manage.alarms.byOperator", { operator }),
      });
      setSelected(new Set());
    } catch (err) {
      pushToast({ level: "critical", title: tr("manage.alarms.bulkAckFailed"), detail: errMsg(err) });
    }
    await refreshAlarms();
  }
  async function bulkClear() {
    const targets = visible.filter((a) => selected.has(a.id) && a.status !== "cleared");
    try {
      await Promise.all(targets.map((a) => adapter.clearAlarm(a.id, operator)));
      pushToast({
        level: "ok",
        title: targets.length === 1 ? tr("manage.alarms.clearOk.one") : tr("manage.alarms.clearOk.many", { count: targets.length }),
        detail: tr("manage.alarms.byOperator", { operator }),
      });
      setSelected(new Set());
    } catch (err) {
      pushToast({ level: "critical", title: tr("manage.alarms.bulkClearFailed"), detail: errMsg(err) });
    }
    await refreshAlarms();
  }
  async function bulkSnooze(ms: number) {
    const targets = visible.filter((a) => selected.has(a.id) && a.status === "active");
    try {
      await Promise.all(targets.map((a) => adapter.snoozeAlarm(a.id, operator, Date.now() + ms)));
      pushToast({
        level: "info",
        title: tr("manage.alarms.snoozedCount", { count: targets.length }),
        detail: tr("manage.alarms.hiddenFor", {
          duration: SNOOZE_OPTIONS.find((s) => s.ms === ms)?.label ?? tr("manage.alarms.aWhile"),
        }),
      });
      setSelected(new Set());
    } catch (err) {
      pushToast({ level: "critical", title: tr("manage.alarms.bulkSnoozeFailed"), detail: errMsg(err) });
    }
    await refreshAlarms();
  }

  async function postComment(a: Alarm) {
    if (!comment.trim()) return;
    try {
      await adapter.commentAlarm(a.id, operator, comment.trim());
    } catch (err) {
      // Keep the draft so the typed note isn't lost on a failed write.
      pushToast({ level: "critical", title: tr("manage.alarms.commentFailed"), detail: errMsg(err) });
      return;
    }
    setComment("");
    // Refresh the row's lazily-fetched live comments too (ALM-03).
    qc.invalidateQueries({ queryKey: ["alarm-comments", a.id] });
    await refreshAlarms();
  }
  async function applyTemplate(a: Alarm, text: string) {
    try {
      await adapter.commentAlarm(a.id, operator, text);
    } catch (err) {
      pushToast({ level: "critical", title: tr("manage.alarms.commentFailed"), detail: errMsg(err) });
      return;
    }
    qc.invalidateQueries({ queryKey: ["alarm-comments", a.id] });
    await refreshAlarms();
  }

  // Hide the snoozed tab entirely when the backend can't snooze — otherwise
  // it is orphaned empty scaffolding (TB has snoozeAlarms:false). TABS stays
  // the URL-decoder source of truth (above); this only filters the rendered
  // segmented control.
  const visibleTabs = caps.snoozeAlarms ? TABS : TABS.filter((tb) => tb !== "snoozed");

  return (
    <PageShell
      title={t("manage.alarms.title")}
      caption={t("manage.alarms.caption")}
      actions={
        // N27 — utility tray: the Export CSV button moves out of the long,
        // wrapping filter row into the actions slot beside the Live | Rules
        // switch (matching Overview's DensityToggle+Export pattern), so the
        // "re-hunt at the end of a long row" case is gone. Its enabled state
        // + title tooltip are preserved verbatim.
        <div className="flex items-center gap-2">
          {/* Symmetric Live | Rules switch (F01): the in-page replacement for
              the demoted "Alarm rules" Manage tab. Live = active non-link
              segment; Rules = the sub-view link. Same token pill chrome as
              the tab strip. */}
          <div className="flex items-center gap-1 rounded-md border border-edge bg-surface p-0.5 text-[11px]">
            <span aria-current="page" className="rounded px-2 py-1 bg-accent/15 text-accent">
              {t("manage.alarms.tabLive")}
            </span>
            <Link to="/alarms/rules" className="rounded px-2 py-1 text-text-muted hover:text-text">
              {t("manage.alarms.tabRules")}
            </Link>
          </div>
          <button
            onClick={() => exportAlarmsCsv(`${topology.shortLabel || topology.name}-alarms`, visible)}
            disabled={!loaded || visible.length === 0}
            title={t("manage.alarms.exportTitle")}
            className="flex items-center gap-1 rounded-md border border-edge bg-surface px-2 py-1 text-[11px] text-text-muted transition hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download size={11} /> {t("common.exportCsv")}
          </button>
        </div>
      }
    >
      {/* Em-dash tiles until the first response — a 0 here before the data
          arrives would be a false all-clear (CONS-05). */}
      <KpiStrip>
        <KpiTile label={t("alarms.active")} value={loaded ? stats.active : "—"} tone={!loaded ? "muted" : stats.active > 0 ? "warn" : "ok"} />
        <KpiTile label={t("alarms.critical")} value={loaded ? stats.critical : "—"} tone={!loaded ? "muted" : stats.critical > 0 ? "critical" : "ok"} />
        {caps.snoozeAlarms && (
          <KpiTile label={t("manage.alarms.kpi.snoozed")} value={loaded ? stats.snoozed : "—"} tone="muted" />
        )}
        <KpiTile label={t("alarms.acknowledged")} value={loaded ? stats.acknowledged : "—"} tone={!loaded ? "muted" : "accent"} />
        <KpiTile label={t("alarms.cleared")} value={loaded ? stats.cleared : "—"} tone="muted" />
      </KpiStrip>

      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle>
            {loaded
              ? `${visible.length === 1 ? t("manage.alarms.inView.one", { count: 1 }) : t("manage.alarms.inView.many", { count: visible.length })}${selected.size > 0 ? ` · ${t("manage.alarms.selectedN", { count: selected.size })}` : ""}`
              : t("manage.alarms.loading")}
          </CardTitle>
          {/* N24a — tab strip stays the primary first-look control; the
              search + device + severity + sort controls collapse into a
              "Filters" disclosure popover (with an active-filter count badge).
              Result row: [tab strip] … [Filters ▾ (n)]. */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex shrink-0 items-center gap-0.5 overflow-x-auto rounded-md border border-edge bg-surface p-0.5 text-[11px]">
              {visibleTabs.map((tb) => (
                <button
                  key={tb}
                  onClick={() => setTab(tb)}
                  className={cn("whitespace-nowrap rounded px-2 py-1", tab === tb ? "bg-accent/15 text-accent" : "text-text-muted hover:text-text")}
                >
                  {t(TAB_LABEL_KEYS[tb])}
                </button>
              ))}
            </div>

            <div className="relative inline-flex">
              <button
                type="button"
                onClick={() => setFiltersOpen((o) => !o)}
                aria-expanded={filtersOpen}
                aria-controls="alarms-filters-popover"
                title={t("manage.alarms.filtersTitle")}
                className={cn(
                  "flex items-center gap-1.5 rounded-md border bg-surface px-2 py-1 text-[11px] transition",
                  activeFilterCount > 0 ? "border-accent/40 text-accent" : "border-edge text-text-muted hover:border-accent hover:text-accent",
                )}
              >
                <Filter size={11} /> {t("manage.alarms.filtersButton")}
                {activeFilterCount > 0 && (
                  <span className="rounded-full bg-accent/15 px-1.5 text-[10px] font-bold text-accent num">{activeFilterCount}</span>
                )}
                <ChevronDown size={11} className={cn("transition-transform", filtersOpen && "rotate-180")} />
              </button>
              {/* Keep the popover content MOUNTED whether open or closed so the
                  data-spa-search input the command palette / automation finds
                  via querySelector stays in the DOM (N24a risk note). When
                  closed it is display:none; focusing it via the global "/"
                  shortcut auto-opens the popover (onFocus below). */}
              {filtersOpen && (
                <button
                  className="fixed inset-0 z-30 cursor-default"
                  onClick={() => setFiltersOpen(false)}
                  aria-hidden
                />
              )}
              <div
                id="alarms-filters-popover"
                className={cn(
                  "absolute right-0 top-full z-40 mt-1 flex max-h-[70vh] w-[min(18rem,calc(100vw-2rem))] flex-col gap-2 overflow-y-auto rounded-md border border-edge bg-surface p-3 shadow-xl",
                  !filtersOpen && "hidden",
                )}
              >
                <div className="flex items-center gap-1 rounded-md border border-edge bg-surface px-2 py-1">
                  <Search size={11} className="text-text-muted" />
                  <input
                    data-spa-search="1"
                    placeholder={t("manage.alarms.filterPlaceholder")}
                    value={filter}
                    onFocus={() => setFiltersOpen(true)}
                    onChange={(e) => setFilter(e.target.value)}
                    className="w-full bg-transparent text-[11px] outline-none placeholder:text-text-faint"
                  />
                </div>
                <select
                  value={device ?? ""}
                  onChange={(e) => setDevice(e.target.value || null)}
                  className="rounded-md border border-edge bg-surface px-2 py-1 text-[11px]"
                >
                  <option value="">{t("manage.alarms.allDevices")}</option>
                  {devices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <select
                  value={severity ?? ""}
                  onChange={(e) => setSeverity(e.target.value || null)}
                  className="rounded-md border border-edge bg-surface px-2 py-1 text-[11px]"
                >
                  {/* Option VALUES are the ?severity= wire params — English only. */}
                  <option value="">{t("manage.alarms.allSeverities")}</option>
                  <option value="critical">{t("manage.sev.critical")}</option>
                  <option value="major">{t("manage.sev.major")}</option>
                  <option value="minor">{t("manage.sev.minor")}</option>
                  <option value="warning">{t("manage.sev.warning")}</option>
                </select>
                <select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value as any)}
                  className="rounded-md border border-edge bg-surface px-2 py-1 text-[11px]"
                >
                  <option value="severity">{t("manage.alarms.sort.severity")}</option>
                  <option value="newest">{t("manage.alarms.sort.newest")}</option>
                  <option value="oldest">{t("manage.alarms.sort.oldest")}</option>
                </select>
              </div>
            </div>
          </div>
        </CardHeader>

        {/* N24b — answer-first triage banner: the worst active alarm + its
            suggested next step, so "what do I do" is on first look with zero
            clicks. Cites the SAME incident as HealthBanner/IncidentStrip
            (useWorstAlarm). "Review" expands+scrolls to the row (reuses the
            ?id= path); "Acknowledge" is a one-click quick action. Hidden on
            cleared/all tabs (no live triage applies). */}
        {showAdvisor && topActive && (
          <div className="flex flex-wrap items-start gap-3 border-b border-warn/30 bg-warn/[0.06] px-4 py-3">
            <span className={cn("mt-0.5 inline-grid h-7 w-7 shrink-0 place-items-center rounded-md border", severityBadgeClass(topActive.severity))}>
              {(() => { const I = SEV_ICON[topActive.severity]; return <I size={14} />; })()}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider text-text-faint font-semibold">{t("manage.alarms.advisorLabel")}</div>
              <div className="text-sm font-medium text-text">
                {topActive.device_name} — {topActive.message}
              </div>
              <div className="mt-0.5 text-[11px] text-text-muted">
                <span className="font-semibold text-text-faint">{t("manage.alarms.nextStepLabel")}</span>{" "}
                {t(nextStepKey(topActive.rule_type))}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={() => reviewAlarm(topActive.id)}
                className="flex items-center gap-1 rounded-md border border-edge bg-surface px-2 py-1 text-[11px] text-text-muted transition hover:border-accent hover:text-accent"
              >
                {t("manage.alarms.advisorReview")} <ArrowRight size={11} />
              </button>
              <button
                onClick={() => ack(topActive)}
                className="flex items-center gap-1 rounded-md border border-accent/40 bg-accent/15 px-2 py-1 text-[11px] text-accent hover:bg-accent/25"
              >
                <Check size={11} /> {t("common.acknowledge")}
              </button>
            </div>
          </div>
        )}

        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-b border-edge bg-accent/5 px-4 py-2 text-[11px]">
            <span className="text-text-muted">{t("manage.alarms.selectedColon", { count: selected.size })}</span>
            <button onClick={bulkAck} className="flex items-center gap-1 rounded border border-accent/40 bg-accent/15 px-2 py-1 text-accent hover:bg-accent/25">
              <Check size={11} /> {t("common.acknowledge")}
            </button>
            {/* Bulk Clear erases state across EVERY selected alarm at once, so
                it gets the hold-to-confirm gate too (the adjacent "Selected: N"
                shows the count being confirmed). bulkAck stays single-click —
                ack is non-destructive. N15: warn tone keeps Clear off the
                grid-export token. */}
            <HoldToConfirm
              label={<span className="inline-flex items-center gap-1"><X size={11} /> {t("common.clear")}</span>}
              intent="warn"
              className="h-7 px-2 text-[11px] normal-case tracking-normal"
              onConfirm={() => void bulkClear()}
            />
            {/* Snooze is hidden entirely when the backend can't do it —
                a placebo button that toasts success is worse than none
                (ALM-03). */}
            {caps.snoozeAlarms && (
              <>
                <span className="ml-2 text-text-faint">{t("common.snooze")}:</span>
                {SNOOZE_OPTIONS.map((o) => (
                  <button key={o.ms} onClick={() => bulkSnooze(o.ms)} className="rounded border border-edge bg-surface px-2 py-1 hover:border-accent/40">
                    {o.label}
                  </button>
                ))}
              </>
            )}
            <button onClick={() => setSelected(new Set())} className="ml-auto text-text-muted hover:text-text">{t("common.cancel")}</button>
          </div>
        )}

        <div className="divide-y divide-edge">
          {/* First fetch in flight → say "loading", never a false
              "No alarms in this view" (CONS-05). */}
          {!loaded && (
            <div className="px-4 py-10 text-center text-xs text-text-muted">
              <Bell size={20} className="mx-auto mb-2 text-text-faint" />
              {t("manage.alarms.loadingDetail")}
            </div>
          )}
          {loaded && visible.length === 0 && (
            <div className="px-4 py-10 text-center text-xs text-text-muted">
              <Bell size={20} className="mx-auto mb-2 text-text-faint" />
              {t("manage.alarms.emptyView")} {(device || severity || filter) && t("manage.alarms.tryClearing")}
            </div>
          )}
          {visible.length > 0 && (
            <div className="flex items-center gap-2 border-b border-edge bg-surface-2/40 px-4 py-1.5 text-[10px] text-text-faint">
              <input
                type="checkbox"
                checked={selected.size > 0 && selected.size === visible.length}
                onChange={selectAllVisible}
                className="h-3 w-3 accent-accent"
                aria-label={t("manage.alarms.selectAllVisible")}
              />
              <span>{selected.size === visible.length ? t("manage.alarms.deselectAll") : t("manage.alarms.selectAllVisible")}</span>
              {(device || severity) && (
                <span className="ml-auto flex items-center gap-1">
                  <Filter size={10} />
                  {t("manage.alarms.filtered")}{device && ` · ${t("manage.alarms.deviceWord")} ${deviceName}`}{severity && ` · ${t(`manage.sev.${severity}`)}`}
                  <button onClick={() => { setDevice(null); setSeverity(null); }} className="ml-2 rounded px-1.5 py-0.5 hover:bg-surface-2">{t("manage.alarms.clearFilters")}</button>
                </span>
              )}
            </div>
          )}
          {visible.map((a) => (
            <AlarmRow
              key={a.id}
              alarm={a}
              now={now}
              expanded={openId === a.id}
              selected={selected.has(a.id)}
              highlight={highlightId === a.id}
              comment={openId === a.id ? comment : ""}
              onCommentChange={setComment}
              onToggleSelect={() => toggleSelect(a.id)}
              onToggle={() => setOpenId(openId === a.id ? null : a.id)}
              onFilterDevice={() => setDevice(a.device_id)}
              onAck={() => ack(a)}
              onClear={() => clear(a)}
              onSnooze={(ms) => snooze(a, ms)}
              onPostComment={() => postComment(a)}
              onTemplate={(text) => applyTemplate(a, text)}
            />
          ))}
        </div>
      </Card>
    </PageShell>
  );
}

function KpiTile({ label, value, tone }: { label: string; value: number | string; tone: "ok" | "warn" | "critical" | "accent" | "muted" }) {
  const cls = {
    ok: "text-ok",
    warn: "text-warn",
    critical: "text-critical",
    accent: "text-accent",
    muted: "text-text",
  }[tone];
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-faint font-semibold">{label}</div>
      <div className={cn("mt-1 text-3xl font-bold num", cls)}>{value}</div>
    </div>
  );
}

function AlarmRow({
  alarm, now, expanded, selected, highlight, onToggle, onToggleSelect, onFilterDevice, onAck, onClear, onSnooze, onPostComment, onTemplate, comment, onCommentChange,
}: {
  alarm: Alarm;
  now: number;
  expanded: boolean;
  selected: boolean;
  highlight: boolean;
  onToggle: () => void;
  onToggleSelect: () => void;
  onFilterDevice: () => void;
  onAck: () => void;
  onClear: () => void;
  onSnooze: (ms: number) => void;
  onPostComment: () => void;
  onTemplate: (s: string) => void;
  comment: string;
  onCommentChange: (s: string) => void;
}) {
  const t = useT();
  const adapter = useAdapter();
  const caps = alarmCaps(adapter);
  const Icon = SEV_ICON[alarm.severity];
  const isSnoozed = alarm.snoozed_until && alarm.snoozed_until > now;
  // ±30 min forensic window around raise time, end clamped to now (ALM-01).
  const winStart = alarm.raised_at - 30 * 60_000;
  const winEnd = Math.min(now, alarm.raised_at + 30 * 60_000);
  const bessId = expanded ? adapter.bessIdForAlarm(alarm) : null;
  // N23 — generalised drill: bess → /bess/:id, grid → /historical (grid_W),
  // controller → /controller. Null when nothing maps (only the Historical
  // link remains). Resolved via the adapter so it's correct in mock AND TB.
  const deviceTarget = expanded ? adapter.deviceTargetForAlarm(alarm) : null;
  // R16 — the catalog key ('soc'/'ac_active_power') the alarm maps to, so the
  // bess device drill can carry the offending series like its sibling "View in
  // Historical" does. null when nothing maps (BessDetail falls back gracefully).
  const catKey = expanded ? alarmCatalogKey(alarm, bessId) : null;
  // N08 + N25 — "Adjust dispatch →" lever for non-cleared, action-shaped alarms.
  const lever = expanded ? dispatchLeverFor(alarm) : null;
  // Live comment read-back (ALM-03): when the backend supports comments AND
  // exposes the lazy fetch, load them on row expand — `alarm.comments` is
  // always [] for live alarms, so a posted note would otherwise vanish on
  // the next poll. Mock alarms carry comments inline and keep that path.
  const commentsFetcher = (adapter as AdapterWithCaps).alarmComments;
  const useLiveComments = caps.alarmComments && typeof commentsFetcher === "function";
  const { data: liveComments } = useQuery<AlarmComment[]>({
    queryKey: ["alarm-comments", alarm.id],
    queryFn: () => commentsFetcher!.call(adapter, alarm.id),
    enabled: expanded && useLiveComments,
    staleTime: 15_000,
  });
  const comments = useLiveComments ? liveComments ?? [] : alarm.comments;
  return (
    <div
      id={`alarm-row-${alarm.id}`}
      className={cn("transition-colors", selected && "bg-accent/5", highlight && "bg-accent/10 ring-2 ring-inset ring-accent/60")}
    >
      <div className="grid grid-cols-[auto_auto_1fr_auto_auto] items-center gap-3 px-4 py-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          onClick={(e) => e.stopPropagation()}
          className="h-3 w-3 accent-accent"
          aria-label={t("manage.alarms.selectAlarm")}
        />
        <span className={cn("inline-grid h-7 w-7 place-items-center rounded-md border", severityBadgeClass(alarm.severity))}>
          <Icon size={14} />
        </span>
        <div>
          <button onClick={onToggle} className="text-left transition hover:text-accent">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{alarm.message}</span>
              {/* No chip when the rule type is unknown — better than
                  asserting "threshold" for every live alarm (ALM-04). */}
              {knownRuleType(alarm.rule_type) && (
                <span className="rounded-full bg-surface-2 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-text-faint">
                  {alarm.rule_type}
                </span>
              )}
              <StatusBadge status={alarm.status} />
              {isSnoozed && (
                <span className="flex items-center gap-1 rounded-full border border-accent/30 bg-accent/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-accent">
                  <Clock size={9} /> {t("manage.alarms.snoozedBadge", { time: formatRelativeFuture(alarm.snoozed_until! - now) })}
                </span>
              )}
            </div>
          </button>
          <div className="mt-0.5 text-[11px] text-text-muted">
            <button
              onClick={onFilterDevice}
              title={t("manage.alarms.showOnlyDevice", { device: alarm.device_name })}
              className="underline decoration-dotted underline-offset-2 hover:text-accent"
            >
              {alarm.device_name}
            </button>
            {" · "}
            <code className="font-mono text-text-faint">{alarm.source_topic}</code> · {t("manage.alarms.raisedAgo", { age: formatRelativeAge(now - alarm.raised_at) })}
          </div>
        </div>
        <div className="text-[11px] text-text-muted">{formatDateTime(alarm.raised_at)}</div>
        <button onClick={onToggle} className="grid h-7 w-7 place-items-center rounded text-text-muted transition hover:bg-surface-2" aria-label={expanded ? t("manage.alarms.collapse") : t("manage.alarms.expand")}>
          <ChevronDown size={14} className={cn("transition-transform", expanded && "rotate-180")} />
        </button>
      </div>
      {expanded && (
        <div className="grid grid-cols-1 gap-4 border-t border-edge bg-surface-2/40 px-4 py-4 lg:grid-cols-[1fr_320px]">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-faint font-semibold">{t("manage.alarms.detail")}</div>
            <p className="mt-1 text-sm text-text">{alarm.detail}</p>
            {/* Generic, safe per-rule-type triage guidance (F09) — never
                site-specific. Falls back to the generic line for an
                unknown/empty rule_type from TB. */}
            <div className="mt-2 flex items-start gap-1.5 text-[11px] text-text-muted">
              <ArrowRight size={12} className="mt-0.5 shrink-0 text-text-faint" aria-hidden />
              <span><span className="font-semibold text-text-faint">{t("manage.alarms.nextStepLabel")}</span>{" "}{t(nextStepKey(alarm.rule_type))}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-[11px]">
              <Field label={t("manage.alarms.field.severity")} value={t(`manage.sev.${alarm.severity}`)} />
              <Field label={t("manage.alarms.field.status")} value={t(`manage.alarms.status.${alarm.status}`)} />
              <Field label={t("manage.alarms.field.sourceTopic")} value={alarm.source_topic} mono />
              {knownRuleType(alarm.rule_type) && <Field label={t("manage.alarms.field.ruleType")} value={alarm.rule_type} mono />}
              <Field label={t("manage.alarms.field.device")} value={alarm.device_name} />
              <Field label={t("manage.alarms.field.raisedAt")} value={formatDateTime(alarm.raised_at)} />
              {alarm.ack_at && <Field label={t("manage.alarms.field.acknowledged")} value={`${formatDateTime(alarm.ack_at)} · ${alarm.ack_by ?? "?"}`} />}
              {alarm.cleared_at && <Field label={t("manage.alarms.field.cleared")} value={formatDateTime(alarm.cleared_at)} />}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {alarm.status !== "acknowledged" && alarm.status !== "cleared" && (
                <button onClick={onAck} className="flex h-8 items-center gap-1.5 rounded-md border border-accent/40 bg-accent/15 px-3 text-[11px] text-accent hover:bg-accent/25">
                  <Check size={12} /> {t("common.acknowledge")}
                </button>
              )}
              {alarm.status !== "cleared" && (
                // Clear is the one state-erasing alarm write (it drops the
                // alarm from the active view and severs its comment/audit
                // thread from any re-raise), so it gets the same hold-to-confirm
                // gate as every other destructive write in the app — Ack/Snooze
                // stay single-click (non-destructive). `warn` (not destructive)
                // because TB re-raises a still-true condition: lost state, not
                // lost plant. N15: the warn tone keeps it off the grid-export token.
                <HoldToConfirm
                  label={<span className="inline-flex items-center gap-1.5"><X size={12} /> {t("common.clear")}</span>}
                  intent="warn"
                  className="h-8 px-3 text-[11px] normal-case tracking-normal"
                  onConfirm={onClear}
                />
              )}
              {/* Hidden when the backend can't snooze — never a placebo
                  control that toasts success for a no-op (ALM-03). */}
              {caps.snoozeAlarms && alarm.status === "active" && (
                <div className="flex items-center gap-1 rounded-md border border-edge bg-surface px-1.5 py-1">
                  <Clock size={11} className="text-text-muted" />
                  <span className="mr-1 text-[10px] text-text-muted">{t("common.snooze")}</span>
                  {SNOOZE_OPTIONS.map((s) => (
                    <button key={s.ms} onClick={() => onSnooze(s.ms)} className="rounded px-1.5 py-0.5 text-[10px] text-text-muted hover:bg-surface-2 hover:text-text">{s.label}</button>
                  ))}
                </div>
              )}
              {/* N23 — generalised "Open device / View meter / Open controller"
                  drill. The forensic window rides along on every kind; the grid
                  drill also preselects the grid_W trace so /historical opens on
                  the meter that contextualises the alarm. The bess drill carries
                  a `from=alarm:<id>` return token (N22) so /bess/:id can offer a
                  "Back to alarm" breadcrumb. Renders nothing when the alarm maps
                  to no openable surface (honesty fallback). */}
              {deviceTarget && (
                <Link
                  to={carryTo(deviceTarget.route, {
                    start: String(winStart),
                    end: String(winEnd),
                    gran: "raw",
                    preset: "custom",
                    ...(deviceTarget.kind === "grid"
                      ? { keys: alarmHistoricalKeys(adapter, alarm, bessId) }
                      : {}),
                    // R16 — bess drill carries the from-token AND (when the
                    // alarm maps to one) the catalog series key, matching the
                    // sibling "View in Historical" depth.
                    ...(deviceTarget.kind === "bess"
                      ? { from: `alarm:${alarm.id}`, ...(catKey ? { keys: catKey } : {}) }
                      : {}),
                  })}
                  className="flex h-8 items-center gap-1.5 rounded-md border border-edge bg-surface px-3 text-[11px] text-text-muted hover:border-accent/40 hover:text-accent"
                >
                  {t(deviceTarget.labelKey)} <ArrowRight size={12} />
                </Link>
              )}
              <Link
                to={carryTo("/historical", { keys: alarmHistoricalKeys(adapter, alarm, bessId), start: String(winStart), end: String(winEnd), gran: "raw", preset: "custom" })}
                className="flex h-8 items-center gap-1.5 rounded-md border border-edge bg-surface px-3 text-[11px] text-text-muted hover:border-accent/40 hover:text-accent"
              >
                {t("alarms.viewInHistorical")} <ArrowRight size={12} />
              </Link>
              {/* N08 + N25 — give the FM a path to ACT: "Adjust dispatch →".
                  peak_demand pre-picks PeakShaving via ?suggest= (mints a draft
                  + opens the EntryEditor); the other action-shaped types open the
                  OverridePanel via ?override=1 (no mode assumed). Same low-emphasis
                  bordered chrome as the sibling drill links; flex-wraps in the
                  narrow TB column. HoldToConfirm still guards the actual write. */}
              {lever && (
                <Link
                  to={carryTo("/schedule", "suggestMode" in lever
                    ? { suggest: lever.suggestMode }
                    // R06 — carry the alarm's reason + id so SchedulePage can
                    // prefill the OverridePanel reason field and show a "from
                    // alarm" origin note, instead of landing on a blank panel
                    // with no "why". The reason is the human-readable message.
                    : { override: "1", reason: `${alarm.device_name} — ${alarm.message}`, alarmId: alarm.id })}
                  className="flex h-8 items-center gap-1.5 rounded-md border border-edge bg-surface px-3 text-[11px] text-text-muted hover:border-accent/40 hover:text-accent"
                >
                  {t("alarms.adjustDispatch")} <ArrowRight size={12} />
                </Link>
              )}
            </div>
          </div>
          <div className="rounded-md border border-edge bg-surface p-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-faint font-semibold">
              <MessageSquare size={11} /> {t("manage.alarms.notes", { count: comments.length })}
            </div>
            {caps.alarmComments ? (
              <>
                <div className="mt-2 max-h-44 space-y-2 overflow-y-auto">
                  {comments.length === 0 && <div className="text-[11px] text-text-muted">{t("manage.alarms.noNotes")}</div>}
                  {comments.map((c) => (
                    <div key={c.id} className="rounded-md bg-surface-2 px-2.5 py-2 text-[11px]">
                      <div className="flex items-center justify-between">
                        <strong>{c.by}</strong>
                        <span className="text-text-faint">{formatRelativeAge(now - c.at)}</span>
                      </div>
                      <p className="mt-1 text-text">{c.text}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {/* Chip shows the part before " — "; the FULL translated
                      text is what gets posted as the operator note. */}
                  {NOTE_TEMPLATE_KEYS.map((k) => {
                    const full = t(k);
                    return (
                      <button key={k} onClick={() => onTemplate(full)} className="rounded border border-edge bg-surface-2 px-1.5 py-0.5 text-[9px] text-text-muted hover:border-accent/40 hover:text-text">{full.split(" — ")[0]}</button>
                    );
                  })}
                </div>
                <div className="mt-2 flex items-center gap-1">
                  <input
                    value={comment}
                    onChange={(e) => onCommentChange(e.target.value)}
                    placeholder={t("manage.alarms.addNote")}
                    className="flex-1 rounded-md border border-edge bg-surface-2 px-2 py-1.5 text-[11px] outline-none focus:border-accent/50"
                    onKeyDown={(e) => { if (e.key === "Enter") onPostComment(); }}
                  />
                  <button onClick={onPostComment} className="rounded-md border border-accent/40 bg-accent/15 px-2 py-1 text-[11px] text-accent hover:bg-accent/25">{t("manage.alarms.post")}</button>
                </div>
              </>
            ) : (
              // Composer hidden when unsupported: a note that vanishes on the
              // next poll is a placebo audit trail (ALM-03).
              <div className="mt-2 text-[11px] text-text-muted">
                {t("manage.alarms.notesUnsupported")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: AlarmStatus }) {
  const t = useT();
  const cls = {
    active: "bg-critical/15 text-critical border-critical/30",
    acknowledged: "bg-accent/15 text-accent border-accent/30",
    cleared: "bg-ok/15 text-ok border-ok/30",
  }[status];
  return <span className={cn("rounded-full border px-2 py-0.5 text-[9px] uppercase tracking-wider", cls)}>{t(`manage.alarms.status.${status}`)}</span>;
}

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-text-faint">{label}</div>
      <div className={cn("font-semibold", mono ? "font-mono text-text" : "text-text")}>{value}</div>
    </div>
  );
}

/**
 * Derive Historical analyzer keys from an alarm: SOC topics chart the unit's
 * SOC, power-ish battery topics its AC active power (both translated to
 * device:<id>:<rawKey> keys via the adapter), anything else falls back to
 * the curated site-level series — always valid, no device-catalog dependency.
 */
function alarmHistoricalKeys(adapter: EmsAdapter, alarm: Alarm, bessId: string | null): string {
  if (bessId) {
    const catalogKey = alarmCatalogKey(alarm, bessId);
    if (catalogKey) {
      const keys = adapter.historicalKeysFor(bessId, [catalogKey]);
      if (keys.length) return keys.join(",");
    }
  }
  return "grid_W,battery/aggregation/power";
}

/**
 * R16 — the CATALOG key ('soc' / 'ac_active_power') an alarm maps to, BEFORE
 * the historicalKeysFor() device-namespacing. The bess device drill
 * (/bess/:id) validates `keys` against catalog keys (BessDetailPage), so it
 * needs this intermediate value — NOT alarmHistoricalKeys' device-namespaced
 * output, which BessDetail would drop. Returns null when nothing maps (the
 * drill then opens on BessDetail's own default selection). Kept in sync with
 * alarmHistoricalKeys' mapping above.
 */
function alarmCatalogKey(alarm: Alarm, bessId: string | null): string | null {
  if (!bessId) return null;
  const tail = alarm.source_topic.split("/").pop() ?? "";
  return /^battery\/measurement\/\d+\/SOC$/i.test(alarm.source_topic)
    ? "soc"
    : alarm.source_topic.startsWith("battery/") && /(power|_w|_kw)$/i.test(tail)
      ? "ac_active_power"
      : null;
}
